import posthog from "posthog-js";
import UserActions from "@/actions/UserActions";
import AppConfig from "../config/app";
import {
  getAccountData,
  isAdmin,
  isContributing,
  isServiceEnabled,
  mergeAccountData,
} from "../hooks/query/useAccount";

/** Intercom, PostHog, and Satismeter lifecycle and action adapters. */

// ---- Component-dispatched PostHog convenience wrappers (pre-existing) ----

export const browseDesignsClicked = () => {
  UserActions.trackPostHogEvent("browse_designs_clicked", {
    location: "signup_modal",
    demoMode: AppConfig.demoMode,
  });
};

export const createAccountClicked = () => {
  UserActions.trackPostHogEvent("create_account_clicked", {
    location: "signup_modal",
    demoMode: AppConfig.demoMode,
  });
};

export const signupModalCanceled = () => {
  UserActions.trackPostHogEvent("signup_modal_canceled", {
    location: "signup_modal",
    demoMode: AppConfig.demoMode,
  });
};

export const previewPublishClicked = () => {
  UserActions.trackPostHogEvent("preview_publish_clicked", {
    location: "toolbar",
    demoMode: AppConfig.demoMode,
  });
};

// ---- Intercom ----

/**
 * Whether Intercom should run: not an admin, not demo mode, configured, and the widget loaded.
 * @returns {boolean|Function}
 */
export const isIntercomEnabled = () =>
  !isAdmin() && !AppConfig.demoMode && AppConfig.intercom && window.Intercom;

/**
 * Whether the Intercom messenger is currently open.
 * @returns {boolean}
 */
export const isIntercomOpen = () => !!getAccountData()?.intercomOpen;

/**
 * Writes the Intercom unread-message count into the account cache.
 * @param {number} unreadCount
 */
export const updateUnreadCount = (unreadCount) => {
  mergeAccountData({ messages: unreadCount });
};

/**
 * Boots the Intercom messenger for the logged-in user and wires its callbacks (unread-count, onShow/onHide).
 * @param {Object} user
 */
export const bootIntercom = (user) => {
  if (!isIntercomEnabled()) return;

  const intercomData = {
    app_id: AppConfig.intercom,
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    user_id: user.user_id,
    custom_launcher_selector: ".ic-help-button",
    created_at: Date.parse(user.create_date) / 1000,
    hide_default_launcher: true,
    acting_user: isContributing()
      ? `${user.actingContributor.first_name} ${user.actingContributor.last_name}`
      : null,
    acting_user_id: isContributing() ? user.actingContributor.id : null,
    intercom_user_jwt: user.intercom_user_jwt,
  };
  window.intercomSettings = intercomData;
  window.Intercom("boot", intercomData);
  window.Intercom("hide");
  window.Intercom("onUnreadCountChange", updateUnreadCount);
  window.Intercom("update", {
    last_request_at: parseInt(new Date().getTime() / 1000),
  });

  // Detect when the intercom is opened or closed
  window.Intercom("onShow", () => {
    mergeAccountData({ intercomOpen: true });
  });
  window.Intercom("onHide", () => {
    mergeAccountData({ intercomOpen: false });
  });
};

/** Shuts down the Intercom session. */
export const shutdownIntercom = () => {
  if (isIntercomEnabled()) {
    window.Intercom("shutdown");
  }
};

/**
 * Pushes the account's subscription status into Intercom.
 * @param {string} status
 */
export const updateIntercomStatus = (status) => {
  if (isIntercomEnabled()) {
    window.Intercom("update", { "showit-status": status });
  }
};

/** Launches the Intercom product tour on first design-view entry, then clears the pending flag. */
export const launchTour = () => {
  if (getAccountData()?.showTour && isIntercomEnabled()) {
    window.location.hash = "design";
    window.Intercom("update");
    mergeAccountData({ showTour: false });
  }
};

// ---- PostHog ----

/**
 * Identifies the logged-in user in PostHog.
 * @param {Object} user
 */
export const identifyPostHog = (user) => {
  if (!AppConfig.posthog) return;

  posthog.identify(user.user_id, {
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    userId: user.user_id,
    domain: user.domain,
    accountAgeDays: user.accountAgeDays,
    isContributing: isContributing(),
    actingContributorEmail: isContributing() ? user.actingContributor.email : null,
    actingContributorId: isContributing() ? user.actingContributor.id : null,
    hasLaunched: user.hasLaunched,
    businessName: user.business_name,
    createDate: user.create_date,
  });
};

/** Resets the PostHog session on logout. */
export const resetPostHog = () => {
  if (AppConfig.posthog) {
    posthog.reset();
  }
};

/**
 * Captures a PostHog event.
 * @param {string} event
 * @param {Object} eventInfo
 */
export const capturePostHog = (event, eventInfo) => {
  if (AppConfig.posthog) {
    posthog.capture(event, eventInfo);
  }
};

// ---- Cross-service event tracking ----

/**
 * Tracks an event to Intercom (and Facebook Pixel when flagged).
 * @param {{event: string, meta?: Object}} data
 * @param {Object} [eventInfo]
 */
export const handleTrackEvent = (data, eventInfo) => {
  if (isIntercomEnabled()) {
    window.Intercom("trackEvent", data.event, data.meta);
  }

  // disabling for now to save on posthog usage if (AppConfig.posthog) { posthog.capture(data.event, data.meta); }

  if (Object.hasOwn(eventInfo ?? {}, "fb") && window.fbq) {
    window.fbq("trackCustom", data.event, data.meta);
  }
};

// ---- Satismeter ----

const satismeterTraits = () => {
  const data = getAccountData();
  return {
    name: `${data?.first_name} ${data?.last_name}`,
    email: data?.email,
  };
};

/**
 * Boots Satismeter for the logged-in user.
 * @param {boolean} [forceSurvey]
 */
export const loadSatismeter = (forceSurvey = false) => {
  if (isServiceEnabled("satismeter", "satismeter")) {
    window.satismeter({
      writeKey: AppConfig.satismeter,
      userId: getAccountData()?.user_id,
      traits: satismeterTraits(),
      ...(forceSurvey && { forceSurvey: true }),
    });
  }
};

/** Boots Satismeter and forces a survey. */
export const initSatisMeter = () => loadSatismeter(true);

/**
 * Tracks a Satismeter event.
 * @param {{event: string, clickLocation: string}} options
 */
export const triggerSatismeterEvent = ({ event, clickLocation }) => {
  if (isServiceEnabled("satismeter", "satismeter")) {
    window.satismeter("track", {
      writeKey: AppConfig.satismeter,
      userId: getAccountData()?.user_id,
      event,
      traits: { ...satismeterTraits(), clickLocation },
    });
  }
};

// ---- Action -> analytics bindings.
UserActions.trackPostHogEvent.listen(capturePostHog);
UserActions.trackEvent.listen(handleTrackEvent);
UserActions.triggerSatismeterEvent.listen(triggerSatismeterEvent);
UserActions.launchTour.listen(launchTour);
/**
 * Captured once per publish flow so a started/completed pair agrees on the version.
 * @typedef {Object} PublishTrackingContext
 * @prop {(1|2)=} rendererVersion
 * @prop {string=} siteDesignId
 * @prop {boolean=} hasBlog - Site has a blog, regardless of this publish including it
 */

/**
 * @param {PublishTrackingContext} context
 * @returns {Object}
 */
const publishProps = ({ rendererVersion, siteDesignId, hasBlog } = {}) => ({
  renderer_version: rendererVersion,
  site_design_id: siteDesignId,
  has_blog: hasBlog,
  demoMode: AppConfig.demoMode,
});

/**
 * @param {PublishTrackingContext & {publishedBlog?: boolean}} context
 * @returns {Object}
 */
const siteProps = (context = {}) => ({
  ...publishProps(context),
  published_blog: context.publishedBlog,
});

/**
 * `error_code` is the stable, groupable dimension — the API's numeric envelope code, e.g.
 * @param {Object} context
 * @param {string=} context.error
 * @param {number=} context.errorCode
 * @param {number=} context.errorStatus
 * @returns {Object}
 */
const failureProps = (context = {}) => ({
  error: context.error,
  error_code: context.errorCode,
  error_status: context.errorStatus,
});

/** @param {PublishTrackingContext & {publishedBlog?: boolean}} context */
export const sitePublishStarted = (context) => {
  UserActions.trackPostHogEvent("site_publish_started", siteProps(context));
};

/** @param {PublishTrackingContext & {publishedBlog?: boolean}} context */
export const sitePublishCompleted = (context) => {
  UserActions.trackPostHogEvent("site_publish_completed", siteProps(context));
};

/**
 * @param {PublishTrackingContext & {publishedBlog?: boolean, error?: string, errorCode?: number, errorStatus?: number}} context
 */
export const sitePublishFailed = (context) => {
  UserActions.trackPostHogEvent("site_publish_failed", {
    ...siteProps(context),
    ...failureProps(context),
  });
};

/** @param {PublishTrackingContext} context */
export const blogPublishStarted = (context) => {
  UserActions.trackPostHogEvent("blog_publish_started", publishProps(context));
};

/** @param {PublishTrackingContext} context */
export const blogPublishCompleted = (context) => {
  UserActions.trackPostHogEvent("blog_publish_completed", publishProps(context));
};

/**
 * @param {PublishTrackingContext & {error?: string, errorCode?: number, errorStatus?: number}} context
 */
export const blogPublishFailed = (context) => {
  UserActions.trackPostHogEvent("blog_publish_failed", {
    ...publishProps(context),
    ...failureProps(context),
  });
};
